import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  name: string;
  href: string;
  current: boolean;
  /**
   * Rendered in place of `name`, when a crumb needs to be more than text —
   * a title that animates as it changes, say. `name` is still required: it
   * is what hover, assistive tech and the document title read.
   */
  node?: ReactNode;
}

interface BreadcrumbsContextValue {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
}

const BreadcrumbsContext = createContext<BreadcrumbsContextValue | undefined>(undefined);

export const BreadcrumbsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const location = useLocation();

  // Auto-reset breadcrumbs on route change
  useEffect(() => {
    setBreadcrumbs([]);
  }, [location.pathname]);

  // Memoize context value - keep setBreadcrumbs reference stable
  const value = useMemo(
    () => ({ breadcrumbs, setBreadcrumbs }),
    [breadcrumbs, setBreadcrumbs]
  );

  return (
    <BreadcrumbsContext.Provider value={value}>
      {children}
    </BreadcrumbsContext.Provider>
  );
};

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbsContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbsProvider');
  }
  return context;
};

export const useOptionalBreadcrumbs = () => {
  return useContext(BreadcrumbsContext);
};
